import { PaQuickRegister } from "@/components/PaQuickRegister";
import { PackageFields } from "@/components/PackageSettings";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { SocialLogin, useSocialPending, SocialPendingBanner } from "@/components/auth/SocialLogin";
import { KitProfileForm, KIT_EMPTY, kitRoles, saveKit } from "@/components/KitProfileForm";
import React, { useEffect, useState, createContext, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Building2, Theater, Scale, Users, Sparkles, AlertCircle } from "lucide-react";
import { validateRegistration, serverErrorsToFields } from "@/lib/registerValidation";

const ErrCtx = createContext({});

import { TaxonomyPicker } from "@/components/TaxonomyPicker";

const ROLES = [
  { id:"public_entity", icon: Building2, title:"Ente Pubblico", price:"SEMPRE GRATIS", desc:"Comune, Pro Loco, Ente Culturale/Turistico pubblico" },
  { id:"artist", icon: Theater, title:"Artista / Gruppo", price:"Provalo gratis", desc:"Spettacolo dal vivo, animazione, DJ, performance" },
  { id:"supplier", icon: Scale, title:"Fornitore Servizi", price:"Provalo gratis", desc:"Audio/Luci, Palchi, Transenne, Effetti" },
  { id:"business", icon: Users, title:"P.IVA / Business", price:"Provalo gratis", desc:"Associazione, Agenzia, Locale, Scuola privata" },
  { id:"citizen", icon: Sparkles, title:"Privato Cittadino", price:"Provalo gratis", desc:"Invio richiesta evento gestita dal Portale" },
];

const INITIAL = { hasVat: false, taxonomy: {}, taxonomyOther: {}, hybrid: false, coverageMinKm:0, coverageMaxKm:200, coverageAllItaly:false, travelIncludedKm:0, travelRatePerKm:0.40, baseFeeNet:0, showReviewsPublicly:true, showAverageRatingPublicly:true };

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [role, setRoleState] = useState(() => (["artist", "supplier", "public_entity", "business", "citizen"].includes(params.get("ruolo")) ? params.get("ruolo") : null));
  const [f, setF] = useState(INITIAL);
  const [kit, setKit] = useState(KIT_EMPTY);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const pending = useSocialPending();
  const prefill = (p) => (p ? { ...INITIAL, socialPending: p.id, email: p.email || "", firstName: (p.name || "").split(" ")[0] || "", lastName: (p.name || "").split(" ").slice(1).join(" ") } : INITIAL);
  useEffect(() => { if (pending) { setF(prefill(pending)); } }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps
  const setRole = (r) => { setRoleState(r); setF(prefill(pending)); setKit(KIT_EMPTY); setErrors({}); };

  const set = (k,v) => { setF((p)=>({ ...p, [k]: v })); setErrors((p)=>{ if (!p[k]) return p; const n = { ...p }; delete n[k]; return n; }); };

  const showErrors = (errs) => {
    setErrors(errs);
    toast.error("Controlla i campi evidenziati in rosso");
    setTimeout(() => document.querySelector(".field-error-wrap")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateRegistration(f, role);
    if (Object.keys(errs).length) { showErrors(errs); return; }
    setLoading(true);
    try {
      const u = await register({ ...f, role });
      if (["artist", "supplier"].includes(u.role) && JSON.stringify(kit) !== JSON.stringify(KIT_EMPTY)) {
        try { await saveKit(kit); } catch { toast.error("Non siamo riusciti a salvare attrezzatura ed esigenze: completale dalla Dashboard"); }
      }
      toast.success("Registrazione completata!");
      nav(["artist","supplier","business"].includes(u.role) ? "/abbonamento" : "/dashboard");
    } catch(err) {
      const fieldErrs = serverErrorsToFields(err);
      if (fieldErrs && Object.keys(fieldErrs).length) showErrors(fieldErrs);
      else toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Errore registrazione");
    } finally { setLoading(false); }
  };

  if (!role) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-16">
        <span className="overline text-gold">Iscrizione</span>
        <h1 className="font-display text-4xl font-bold mt-2 mb-2">Scegli il tuo profilo</h1>
        <p className="text-slate-600 mb-6">Il modulo si adatta automaticamente al ruolo selezionato (dati fiscali, codice ATECO, SDI/PEC o CF).</p>
        <SocialPendingBanner pending={pending}/>
        {!pending && <div className="max-w-sm mb-8"><SocialLogin label="Iscriviti"/></div>}
        <div className="grid md:grid-cols-5 gap-5">
          {ROLES.map((r)=>(
            <button key={r.id} data-testid={`choose-role-${r.id}`} onClick={()=>setRole(r.id)} className="card-premium p-6 text-left">
              <r.icon className="w-7 h-7 text-gold mb-4"/>
              <div className="chip-gold mb-3">{r.price}</div>
              <div className="font-display text-xl font-bold">{r.title}</div>
              <p className="text-sm text-slate-600 mt-2">{r.desc}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const isArtist = role === "artist";
  const isSupplier = role === "supplier";
  const isPA = role === "public_entity";
  const isBiz = role === "business";
  const isCitizen = role === "citizen";
  const needsVatFields = (isArtist || isSupplier) ? f.hasVat : isBiz;
  if (isPA) { return <PaQuickRegister onBack={() => setRole(null)} pending={pending}/>; }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <button data-testid="back-to-roles" onClick={()=>setRole(null)} className="block text-sm text-slate-500 hover:text-navy mb-6">← Cambia profilo</button>
      <span className="overline text-gold">Modulo {ROLES.find(r=>r.id===role).title}</span>
      <h1 className="font-display text-3xl font-bold mt-2 mb-8">Completa i tuoi dati</h1>
      <SocialPendingBanner pending={pending}/>
      <ErrCtx.Provider value={errors}>
      {Object.keys(errors).length > 0 && (
        <div className="mb-5 p-4 rounded-xl border border-red-300 bg-red-50 text-red-700 text-sm flex items-start gap-2" data-testid="reg-error-summary">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
          <span>Alcuni campi sono mancanti o non corretti. Correggi le voci evidenziate in rosso.</span>
        </div>
      )}
      <form onSubmit={submit} noValidate className="card-premium p-8 space-y-5">
        {/* Account */}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Email" required name="email"><input data-testid="reg-email" type="email" required readOnly={!!pending?.email} value={f.email || ""} onChange={(e)=>set("email", e.target.value)} className={input}/></Field>
          {!f.socialPending && <Field label="Password" required name="password"><PasswordInput testId="reg-password" required minLength={6} onChange={(e)=>set("password", e.target.value)} className={input}/></Field>}
        </div>

        {/* Personal / Company */}
        {(isArtist || isCitizen) && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nome" required name="firstName"><input data-testid="reg-firstName" required value={f.firstName || ""} onChange={(e)=>set("firstName", e.target.value)} className={input}/></Field>
            <Field label="Cognome" required name="lastName"><input data-testid="reg-lastName" required value={f.lastName || ""} onChange={(e)=>set("lastName", e.target.value)} className={input}/></Field>
          </div>
        )}
        {(isArtist || isSupplier || isBiz) && (
          <Field label={isArtist?"Nome d'arte / Ragione sociale":"Ragione sociale"} required name="companyName">
            <input data-testid="reg-companyName" required onChange={(e)=>set("companyName", e.target.value)} className={input}/>
          </Field>
        )}
        {isPA && (
          <>
            <Field label="Nome Ente / Comune" required name="entityName"><input data-testid="reg-entityName" required onChange={(e)=>set("entityName", e.target.value)} className={input}/></Field>
            <Field label="Nome Referente" required name="referentName"><input data-testid="reg-referentName" required onChange={(e)=>set("referentName", e.target.value)} className={input}/></Field>
          </>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Codice Fiscale" required name="taxCode"><input data-testid="reg-taxCode" required value={f.taxCode || ""} onChange={(e)=>set("taxCode", e.target.value.toUpperCase())} className={input}/></Field>
          <Field label="Città" required name="city"><input data-testid="reg-city" required onChange={(e)=>set("city", e.target.value)} className={input}/></Field>
          {isArtist && <div className="md:col-span-2"><Field label="Indirizzo di residenza dell'artista" required name="residenceAddress"><input data-testid="reg-residenceAddress" required placeholder="Via, numero civico, CAP, comune" onChange={(e)=>set("residenceAddress", e.target.value)} className={input}/></Field></div>}
        </div>

        {/* VAT toggle for artist/supplier */}
        {(isArtist || isSupplier) && (
          <Field label="Hai Partita IVA?">
            <div className="flex gap-3">
              <button type="button" data-testid="reg-hasVat-yes" onClick={()=>set("hasVat", true)} className={`px-4 py-2 rounded-full border ${f.hasVat?"bg-navy text-white border-navy":"border-slate-300"}`}>Sì</button>
              <button type="button" data-testid="reg-hasVat-no" onClick={()=>set("hasVat", false)} className={`px-4 py-2 rounded-full border ${!f.hasVat?"bg-navy text-white border-navy":"border-slate-300"}`}>No</button>
            </div>
          </Field>
        )}

        {needsVatFields && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Partita IVA" required name="vatNumber"><input data-testid="reg-vatNumber" onChange={(e)=>set("vatNumber", e.target.value)} className={input}/></Field>
            <Field label="Codice ATECO" name="atecoCode"><input data-testid="reg-atecoCode" onChange={(e)=>set("atecoCode", e.target.value)} className={input}/></Field>
            <Field label="Dati Fatturazione (indirizzo)"><input data-testid="reg-billingAddress" onChange={(e)=>set("billingAddress", e.target.value)} className={input}/></Field>
            <Field label="Codice Univoco SDI" name="sdiCode"><input data-testid="reg-sdiCode" value={f.sdiCode || ""} onChange={(e)=>set("sdiCode", e.target.value.toUpperCase())} className={input}/></Field>
            <Field label="PEC" name="pec"><input data-testid="reg-pec" type="email" onChange={(e)=>set("pec", e.target.value)} className={input}/></Field>
          </div>
        )}

        {isPA && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Codice Univoco Ufficio IPA" required name="ipaCode"><input data-testid="reg-ipaCode" required value={f.ipaCode || ""} onChange={(e)=>set("ipaCode", e.target.value.toUpperCase())} className={input}/></Field>
            <Field label="PEC" required name="pec"><input data-testid="reg-pec" type="email" required onChange={(e)=>set("pec", e.target.value)} className={input}/></Field>
          </div>
        )}

        {(isPA || isBiz) && (
          <Field label="L'ente è soggetto a Split Payment (scissione dei pagamenti)?" required={isPA} name="splitPayment">
            <div className="flex gap-3">
              <button type="button" data-testid="reg-split-yes" onClick={()=>set("splitPayment", true)} className={`px-4 py-2 rounded-full border ${f.splitPayment===true?"bg-navy text-white border-navy":"border-slate-300"}`}>Sì</button>
              <button type="button" data-testid="reg-split-no" onClick={()=>set("splitPayment", false)} className={`px-4 py-2 rounded-full border ${f.splitPayment===false?"bg-navy text-white border-navy":"border-slate-300"}`}>No</button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Con lo Split Payment l'Ente paga all'operatore solo l'imponibile e versa l'IVA direttamente all'Erario (art. 17-ter DPR 633/72).</p>
          </Field>
        )}

        {/* Artist / Supplier categories */}
        {(isArtist || isSupplier) && (
          <>
            <Field label="Aliquota o regime IVA (mostrato all'acquirente nel preventivo)" required name="vatRegime">
              <select data-testid="reg-vatRegime" value={f.vatRegime || ""} onChange={(e)=>set("vatRegime", e.target.value)} className={input}>
                <option value="">Seleziona…</option>
                <option value="22">IVA 22% (ordinaria)</option>
                <option value="10">IVA 10%</option>
                <option value="5">IVA 5%</option>
                <option value="4">IVA 4%</option>
                <option value="esente">Esente IVA (art. 10 DPR 633/72)</option>
                <option value="forfettario">Regime forfettario (senza IVA)</option>
                <option value="occasionale">Prestazione occasionale (senza P.IVA, senza IVA)</option>
              </select>
            </Field>
            <Field label={isArtist ? "Le tue categorie artistiche (scelta multipla)" : "I tuoi servizi (scelta multipla)"} required name="taxonomy">
              <p className="text-xs text-slate-500 mb-3">Apri le macro-aree e spunta tutte le sottocategorie che ti rappresentano.</p>
              <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer" data-testid="reg-hybrid">
                <input type="checkbox" checked={!!f.hybrid} onChange={(e)=>{ const other = isArtist ? "supplier" : "artist"; const tax = { ...(f.taxonomy||{}) }; if (!e.target.checked) delete tax[other]; setF((p)=>({ ...p, hybrid: e.target.checked, taxonomy: tax })); }} className="accent-[#D5A34C]"/>
                {isArtist ? "Offro anche servizi come Fornitore (es. DJ con service audio)" : "Mi esibisco anche come Artista / Performer"}
              </label>
              <TaxonomyPicker testPrefix="reg-tax" areas={f.hybrid ? (isArtist ? ["artist","supplier"] : ["supplier","artist"]) : [role]}
                value={f.taxonomy || {}} other={f.taxonomyOther || {}}
                onChange={(v, o)=>{ setF((p)=>({ ...p, taxonomy: v, taxonomyOther: o })); setErrors((p)=>{ const n = { ...p }; delete n.taxonomy; return n; }); }}/>
            </Field>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Base Location"><input data-testid="reg-baseLocation" onChange={(e)=>set("baseLocation", e.target.value)} className={input}/></Field>
              <Field label="Compenso base netto (€)" name="baseFeeNet"><input data-testid="reg-baseFeeNet" type="number" step="0.01" onChange={(e)=>set("baseFeeNet", parseFloat(e.target.value)||0)} className={input}/></Field>
              <Field label="Raggio d'azione (km)" name="coverageMaxKm">
                <input data-testid="reg-coverageMaxKm" type="number" min="0" disabled={f.coverageAllItaly} value={f.coverageAllItaly ? "" : f.coverageMaxKm} placeholder={f.coverageAllItaly ? "Tutta Italia" : "es. 100"} onChange={(e)=>set("coverageMaxKm", parseInt(e.target.value)||0)} className={`${input} disabled:opacity-50`}/>
                <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer"><input type="checkbox" data-testid="reg-coverageAllItaly" checked={!!f.coverageAllItaly} onChange={(e)=>set("coverageAllItaly", e.target.checked)} className="accent-[#C98A3C]"/>Tutta Italia</label>
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-4" data-testid="reg-travel">
              <Field label="Quota trasferta · km inclusi nel prezzo" name="travelIncludedKm"><input data-testid="reg-travelIncludedKm" type="number" min="0" value={f.travelIncludedKm} onChange={(e)=>set("travelIncludedKm", parseInt(e.target.value)||0)} placeholder="es. 50" className={input}/></Field>
              <Field label="Tariffa al km oltre la soglia (€)" name="travelRatePerKm"><input data-testid="reg-travelRatePerKm" type="number" min="0" step="0.01" value={f.travelRatePerKm} onChange={(e)=>set("travelRatePerKm", e.target.value === "" ? "" : parseFloat(e.target.value))} placeholder="es. 0.40" className={input}/></Field>
            </div>
            {isArtist && <Field label="Durata prestazione"><input data-testid="reg-performanceDuration" placeholder="es. 40 minuti" onChange={(e)=>set("performanceDuration", e.target.value)} className={input}/></Field>}
            <PackageFields value={{ packageAvailable: f.packageAvailable, packageCommission: f.packageCommission ?? 10 }} onChange={(v)=>setF({ ...f, ...v })}/>
            {(() => { const kr = kitRoles(role, f.taxonomy); return (kr.artist || kr.service) && (
              <div className="pt-6 border-t border-slate-200" data-testid="reg-kit-section">
                <h3 className="font-display text-xl font-bold mb-1">Attrezzatura ed esigenze</h3>
                <p className="text-sm text-slate-600 mb-5">Sezione dedicata ai pacchetti Evento Chiavi in Mano: potrai modificarla in qualsiasi momento dalla Dashboard.</p>
                <KitProfileForm value={kit} onChange={setKit} roles={kr}/>
              </div>); })()}
            <div className="flex gap-6 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.showReviewsPublicly} onChange={(e)=>set("showReviewsPublicly", e.target.checked)}/> Mostra recensioni nel profilo</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.showAverageRatingPublicly} onChange={(e)=>set("showAverageRatingPublicly", e.target.checked)}/> Mostra media voto</label>
            </div>
          </>
        )}

        <div className="pt-4 border-t border-slate-200">
          <button data-testid="reg-submit" disabled={loading} className="btn-pill-gold"><UserPlus className="w-4 h-4"/>{loading?"Invio…":"Completa iscrizione"}</button>
        </div>
      </form>
      </ErrCtx.Provider>
    </div>
  );
}

const input = "w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:outline-none focus:border-gold text-sm";
function Field({ label, required, name, children }) {
  const errors = useContext(ErrCtx);
  const err = name ? errors[name] : null;
  return (
    <div className={err ? "field-error-wrap" : ""} data-testid={name ? `field-${name}` : undefined}>
      <label className="overline text-slate-500 mb-2 block">{label}{required && <span className="text-gold">*</span>}</label>
      {children}
      {err && <div className="field-error-text" data-testid={`reg-error-${name}`}>{err}</div>}
    </div>
  );
}
